import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Row, Col, InputNumber, Divider, Button, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useGroups } from '../../hooks/useGroups';
import { usePackagePlans } from '../../hooks/usePackagePlans';

const { Option } = Select;

// Telefonu öğrenci kaydındaki formata çevirir: sadece rakam, başında 0 yok, 10 hane
const normalizePhone = (phone) =>
    phone ? phone.replace(/\D/g, '').replace(/^90(?=\d{10}$)/, '').replace(/^0/, '').substring(0, 10) : '';

const PreRegistrationConvertModal = ({ open, record, confirmLoading, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [showPackageSection, setShowPackageSection] = useState(false);
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const { ageGroups } = useAgeGroups(selectedBranch?.id);
    const { groups } = useGroups(selectedBranch?.id);
    const { packagePlans } = usePackagePlans(selectedBranch?.id);

    // Doğum yılını içeren yaş grubunu bul (öğrenci formundaki davranışla aynı)
    const findAgeGroupIds = (birthDate) => {
        if (!birthDate) return [];
        const matched = (ageGroups || []).find((ag) => ag.name.includes(birthDate.year().toString()));
        return matched ? [matched.id] : [];
    };

    useEffect(() => {
        if (open && record) {
            const birthDate = record.studentBirthDate ? dayjs(record.studentBirthDate) : null;
            form.resetFields();
            setShowPackageSection(false);
            form.setFieldsValue({
                studentFullName: record.studentFullName,
                studentBirthdate: birthDate,
                parentFullName: record.parentFullName,
                parentPhoneNumber: normalizePhone(record.parentPhoneNumber),
                registrationDate: dayjs(),
                ageGroupIds: findAgeGroupIds(birthDate),
                groupIds: [],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record, form]);

    // Yaş grupları modal açıldıktan sonra yüklenirse boş alanı doldur
    useEffect(() => {
        if (open && record && !(form.getFieldValue('ageGroupIds') || []).length) {
            form.setFieldValue('ageGroupIds', findAgeGroupIds(form.getFieldValue('studentBirthdate')));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ageGroups]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            let packagePrice = values.packagePrice;
            if (values.packagePlanId && !packagePrice) {
                packagePrice = packagePlans.find((p) => p.id === values.packagePlanId)?.price || null;
            }

            onSubmit({
                preRegistrationId: record.id,
                branchId: selectedBranch.id,
                studentFullName: values.studentFullName,
                studentBirthdate: values.studentBirthdate ? values.studentBirthdate.toISOString() : null,
                gender: values.gender || null,
                registrationDate: values.registrationDate ? values.registrationDate.toISOString() : null,
                groupIds: values.groupIds || [],
                ageGroupIds: values.ageGroupIds || [],
                parentFullName: values.parentFullName,
                parentPhoneNumber: normalizePhone(values.parentPhoneNumber) || null,
                packagePlanId: values.packagePlanId || null,
                packagePrice: values.packagePlanId ? packagePrice : null,
                paidAmount: values.packagePlanId ? values.paidAmount || null : null,
                packageStartTime: values.packagePlanId && values.packageStartTime ? values.packageStartTime.toISOString() : null,
                packageEndTime: values.packagePlanId && values.packageEndTime ? values.packageEndTime.toISOString() : null,
                packageNote: values.packagePlanId ? values.packageNote || null : null,
            });
        });
    };

    return (
        <Modal
            title="Öğrenciye Dönüştür"
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={confirmLoading}
            okText="Öğrenci Olarak Kaydet"
            cancelText="İptal"
            width={720}
            destroyOnClose
        >
            {!selectedBranch && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Öğrenci eklemek için önce bir branş seçmelisiniz." />
            )}
            <Form form={form} layout="vertical">
                <Divider orientation="left">Öğrenci</Divider>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="studentFullName" label="Ad Soyad" rules={[{ required: true, message: 'Lütfen ad soyad girin!' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="studentBirthdate" label="Doğum Tarihi">
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                onChange={(date) => form.setFieldValue('ageGroupIds', findAgeGroupIds(date))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="gender" label="Cinsiyet">
                            <Select placeholder="Cinsiyet seçin (opsiyonel)" allowClear>
                                <Option value="Male">Erkek</Option>
                                <Option value="Female">Kadın</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="registrationDate" label="Kayıt Tarihi">
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="ageGroupIds" label="Yaş Grupları">
                            <Select mode="multiple" placeholder="Yaş gruplarını seçin (opsiyonel)" allowClear>
                                {(ageGroups || []).map((ag) => (
                                    <Option key={ag.id} value={ag.id}>{ag.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="groupIds" label="Gruplar">
                            <Select mode="multiple" placeholder="Grupları seçin (opsiyonel)" allowClear>
                                {(groups || []).map((g) => (
                                    <Option key={g.id} value={g.id}>{g.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Veli</Divider>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="parentFullName" label="Veli Adı" rules={[{ required: true, message: 'Lütfen veli adını girin!' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="parentPhoneNumber" label="Veli Telefonu" extra="Başında 0 olmadan, 10 hane">
                            <Input maxLength={15} placeholder="5XXXXXXXXX" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Paket</Divider>
                {!showPackageSection ? (
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setShowPackageSection(true)}>
                        Paket Ekle (opsiyonel)
                    </Button>
                ) : (
                    <>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="packagePlanId" label="Paket Planı">
                                    <Select placeholder="Paket planı seçin" allowClear>
                                        {(packagePlans || []).map((p) => (
                                            <Option key={p.id} value={p.id}>{p.name} - {p.price} TL</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="packagePrice" label="Paket Fiyatı" extra="Boş bırakılırsa plan fiyatı uygulanır">
                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="Paket fiyatı" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="paidAmount" label="Ödenen Miktar">
                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="Ödenen miktar (opsiyonel)" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="packageNote" label="Paket Notu">
                                    <Input placeholder="Paket ile ilgili not" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="packageStartTime" label="Başlangıç Tarihi" extra="Boş bırakılırsa bugün">
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="packageEndTime" label="Bitiş Tarihi" extra="Boş bırakılırsa plan süresi kadar">
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default PreRegistrationConvertModal;
